import ability from '@/libs/acl/ability'
import { DEFAULT_TIME_RANGE_FROM } from '../../utils/timeUtils'
import alarms from './alarms'
import inventories from './inventories'
import operationalProcedures from './operational_procedures'
import operationalSecurity from './operational_security'
import penetrationTestings from './penetration_testings'
import plannings from './plannings'
import policyExceptions from './policy_exceptions'
import riskFormAnswers from './risk_form_answers'
import securityBoxTestings from './security_box_testings'
import trainings from './trainings'
import units from './units'
import securitySelfAudits from './security_self_audits'
import simulacrums from './simulacrums'
import billingFinances from './billing_finances'
import billingPrices from './billing_prices'
import brigade_assignments from './brigade_assignments'
import securityManagmentFacility from './security_managment_facility'
import securityManagmentFireSafetyTraining from './security_managment_fire_safety_training'
import securityManagmentFireWardensTraining from './security_managment_fire_wardens_training'
import securityManagmentAdminTraining from './security_managment_admin_training'
import securityManagmentRiskAssessment from './security_managment_risk_assessment'
import securityManagmentDrill from './security_managment_drill'
import securityManagmentIncident from './security_managment_incident'
import securityManagmentSsa from './security_managment_ssa'
import securityManagmentSra from './security_managment_sra'
import securityManagmentBau from './security_managment_bau'
import securityManagmentCrisis from './security_managment_crisis'
import securityManagmentFlashAlert from './security_managment_flash_alert'
import securityManagmentOre from './security_managment_ore'
import securityManagmentEmployee from './security_managment_employee'
import securityManagmentSitreps from './security_managment_sitreps'
import bauComplementFormAnswers from './bau_complement_form_answers'
import hanwhaLogs from './hanwha_logs'
import hanwhaCameras from './hanwha_cameras'
import dmpAlarms from './dmp_alarms'
import dmpLogs from './dmp_logs'
import dmpPanelDevices from './dmp_panel_devices'

const DATASET_MODULES = {
  [alarms.key]: alarms,
  [inventories.key]: inventories,
  [operationalSecurity.key]: operationalSecurity,
  [penetrationTestings.key]: penetrationTestings,
  [plannings.key]: plannings,
  [policyExceptions.key]: policyExceptions,
  [simulacrums.key]: simulacrums,
  [operationalProcedures.key]: operationalProcedures,
  [securitySelfAudits.key]: securitySelfAudits,
  [riskFormAnswers.key]: riskFormAnswers,
  [trainings.key]: trainings,
  [units.key]: units,
  [brigade_assignments.key]: brigade_assignments,
  [billingFinances.key]: billingFinances,
  [billingPrices.key]: billingPrices,
  [bauComplementFormAnswers.key]: bauComplementFormAnswers,
  [securityBoxTestings.key]: securityBoxTestings,
  [securityManagmentAdminTraining.key]: securityManagmentAdminTraining,
  [securityManagmentBau.key]: securityManagmentBau,
  [securityManagmentCrisis.key]: securityManagmentCrisis,
  [securityManagmentDrill.key]: securityManagmentDrill,
  // [securityManagmentEmployee.key]: securityManagmentEmployee,
  // [securityManagmentFacility.key]: securityManagmentFacility,
  [securityManagmentFireSafetyTraining.key]: securityManagmentFireSafetyTraining,
  [securityManagmentFireWardensTraining.key]: securityManagmentFireWardensTraining,
  [securityManagmentFlashAlert.key]: securityManagmentFlashAlert,
  [securityManagmentIncident.key]: securityManagmentIncident,
  [securityManagmentOre.key]: securityManagmentOre,
  [securityManagmentRiskAssessment.key]: securityManagmentRiskAssessment,
  [securityManagmentSitreps.key]: securityManagmentSitreps,
  [securityManagmentSra.key]: securityManagmentSra,
  [securityManagmentSsa.key]: securityManagmentSsa,
  [hanwhaLogs.key]: hanwhaLogs,
  [hanwhaCameras.key]: hanwhaCameras,
  [dmpAlarms.key]: dmpAlarms,
  [dmpLogs.key]: dmpLogs,
  [dmpPanelDevices.key]: dmpPanelDevices,
}

export function getDatasetModule(key) {
  return DATASET_MODULES[key] || null
}

export function getAllDatasetModules() {
  return DATASET_MODULES
}

/**
 * Where a chart of this dataset starts its time window.
 *
 * A dataset whose time filter rides an updated_at column hides most of its
 * catalog behind the 30-day default: measured on staging, `units` filters on
 * units.updated_at, so the chart draws 9 of 247 installations and its filter
 * dropdowns offer 3 tenants of 20. Such a module declares
 * `defaultTimeRange: { from }`; the user can still narrow it afterwards.
 */
export function getDatasetDefaultFrom(key) {
  return getDatasetModule(key)?.defaultTimeRange?.from || DEFAULT_TIME_RANGE_FROM
}

/**
 * Current spelling of a label restored from a saved chart.
 *
 * A process-form field and its questionnaire are persisted by label, not by
 * key, so renaming one would orphan every slot saved before. The module lists
 * the old spellings in `legacyLabels: { old: current }`.
 */
export function resolveLegacyLabel(key, label) {
  return getDatasetModule(key)?.legacyLabels?.[label] || label
}

/**
 * Resolve a user's access to a dataset against the ACL.
 *
 * A dataset may declare a primary `permission` (full, unrestricted access)
 * and an optional `permissionFallbacks` list — alternative permissions that
 * also grant access but carry restrictions (a forced `where` clause and/or
 * locked filter keys). The primary permission always wins; fallbacks apply
 * only when the primary permission is absent or unsatisfied. A dataset that
 * declares fallbacks is permission-gated even without a primary `permission`
 * (the primary "full access" path is simply unreachable).
 *
 * @param {string} key dataset key
 * @returns {{ allowed: boolean, restriction: ({ forcedWhere: object|null, lockedFilters: string[] } | null) }}
 */
export function getDatasetAccess(key) {
  const module = DATASET_MODULES[key]
  if (!module) return { allowed: false, restriction: null }

  const fallbacks = module.permissionFallbacks || []

  // No permission gating at all → open to everyone, unrestricted.
  if (!module.permission && fallbacks.length === 0) {
    return { allowed: true, restriction: null }
  }

  // Primary permission (when declared) grants full, unrestricted access.
  if (module.permission && ability.can(module.permission.action, module.permission.subject)) {
    return { allowed: true, restriction: null }
  }

  // Otherwise fall back to the first matching alternative permission and
  // carry its restrictions.
  const fallback = fallbacks.find(
    fb => fb.permission && ability.can(fb.permission.action, fb.permission.subject),
  )
  if (fallback) {
    return {
      allowed: true,
      restriction: {
        forcedWhere: fallback.forcedWhere || null,
        lockedFilters: fallback.lockedFilters || [],
      },
    }
  }

  return { allowed: false, restriction: null }
}

export function getDatasetOptions() {
  return Object.values(DATASET_MODULES).map(m => ({
    value: m.key,
    label: m.label,
  }))
}

/**
 * Check if a field definition is a date/time field.
 * A field is considered date-related if it has defaultParams.freq
 * or its key contains common date column patterns.
 */
function isDateField(fieldDef) {
  if (fieldDef.defaultParams && fieldDef.defaultParams.freq) return true
  const k = fieldDef.key.toLowerCase()
  return k.endsWith('.created_at') || k.endsWith('.updated_at') || k.includes('_date') || k.includes('.date')
}

/**
 * For timeseries view: split a dataset's fields into date fields (for campo 1)
 * and non-date fields (for campo 2).
 *
 * @param {string} key - dataset key
 * @returns {{ dateFields: Array, otherFields: Array } | null}
 */
export function getTimeseriesFields(key) {
  const module = DATASET_MODULES[key]
  if (!module || !module.fields) return null

  const dateFields = module.fields.filter(f => !f.processForm && isDateField(f))
  const otherFields = module.fields.filter(f => !f.processForm && !isDateField(f))

  return { dateFields, otherFields }
}

/**
 * Get the default created_at field key for a dataset's timeseries view.
 * Uses the module's timeColumn, or falls back to the first date field.
 *
 * @param {string} key - dataset key
 * @returns {string} field key for the default time grouping
 */
export function getTimeseriesDefaultField(key) {
  const module = DATASET_MODULES[key]
  if (!module) return ''

  // Prefer timeColumn defined on the module
  if (module.timeColumn) {
    const hasField = module.fields?.some(f => f.key === module.timeColumn)
    if (hasField) return module.timeColumn
  }

  // Fallback: first date field
  const tsFields = getTimeseriesFields(key)
  return tsFields?.dateFields?.[0]?.key || ''
}

export default DATASET_MODULES
